// Text-to-speech via the Web Speech API (browser SpeechSynthesis). Picks a
// natural-sounding English voice when available.
//
// Ported from FlowStateNexus src/hooks/useTextToSpeech.js (architecture
// §11.3.3) and converted to typed TSX. This is the free/browser tier; the
// Google-TTS quality path (Pro/Teams) is a separate provider slice.

import { useCallback, useEffect, useRef, useState } from 'react';

export interface SpeakOptions {
  voice?: SpeechSynthesisVoice;
  rate?: number; // 0.1–10
  pitch?: number; // 0–2
  volume?: number; // 0–1
}

export interface TextToSpeechState {
  isSpeaking: boolean;
  isSupported: boolean;
  voices: SpeechSynthesisVoice[];
  selectedVoice: SpeechSynthesisVoice | null;
  error: string | null;
  speak: (text: string, options?: SpeakOptions) => Promise<void>;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  setSelectedVoice: (v: SpeechSynthesisVoice) => void;
}

function pickPreferredVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  return (
    voices.find(
      (v) =>
        v.lang.startsWith('en') &&
        (v.name.includes('Natural') ||
          v.name.includes('Premium') ||
          v.name.includes('Enhanced') ||
          v.name.includes('Samantha') || // macOS
          v.name.includes('Google')),
    ) ??
    voices.find((v) => v.lang.startsWith('en-US')) ??
    voices.find((v) => v.lang.startsWith('en')) ??
    voices[0] ??
    null
  );
}

export function useTextToSpeech(): TextToSpeechState {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      setIsSupported(false);
      setError('Text-to-speech is not supported in this browser');
      return;
    }
    setIsSupported(true);

    const loadVoices = () => {
      const available = window.speechSynthesis.getVoices();
      setVoices(available);
      setSelectedVoice((prev) => prev ?? pickPreferredVoice(available));
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.cancel();
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  const speak = useCallback(
    (text: string, options: SpeakOptions = {}): Promise<void> => {
      if (typeof window === 'undefined' || !window.speechSynthesis) {
        setError('Speech synthesis not available');
        return Promise.reject(new Error('Speech synthesis not available'));
      }
      window.speechSynthesis.cancel();

      return new Promise<void>((resolve, reject) => {
        const utterance = new SpeechSynthesisUtterance(text);
        const voice = options.voice ?? selectedVoice;
        if (voice) utterance.voice = voice;
        utterance.rate = options.rate ?? 1.0;
        utterance.pitch = options.pitch ?? 1.0;
        utterance.volume = options.volume ?? 1.0;

        utterance.onstart = () => {
          setIsSpeaking(true);
          setError(null);
        };
        utterance.onend = () => {
          setIsSpeaking(false);
          resolve();
        };
        utterance.onerror = (event) => {
          setIsSpeaking(false);
          setError(`Speech synthesis error: ${event.error}`);
          reject(new Error(event.error));
        };

        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
      });
    },
    [selectedVoice],
  );

  const stop = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, []);

  const pause = useCallback(() => window.speechSynthesis?.pause(), []);
  const resume = useCallback(() => window.speechSynthesis?.resume(), []);

  return {
    isSpeaking,
    isSupported,
    voices,
    selectedVoice,
    error,
    speak,
    stop,
    pause,
    resume,
    setSelectedVoice,
  };
}
